import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { CookieType } from "../types/types";
import type { CurrentView } from "../types/types";

/**
 * PersistedViewStore contains data that needs to be persisted between session.
 * For example flags that show if a user has already seen a survey.
 */
interface PersistedViewStore {
  exitSurveyHasBeenShown: boolean;
  setExitSurveyHasBeenShown: (s: boolean) => void;
  feedbackSurveyHasBeenShown: boolean;
  setFeedbackSurveyHasBeenShown: (b: boolean) => void;
  npsScoreHasBeenSet: boolean;
  setNpsScoreHasBeenSet: (s: boolean) => void;
  measurementHasBeenShown: boolean;
  setMeasurementHasBeenShown: (s: boolean) => void;
  cookieType: CookieType;
  setCookieType: (m: CookieType) => void;
}

export const usePersistedViewStore = create<PersistedViewStore>()(
  devtools(
    persist(
      (set) => ({
        exitSurveyHasBeenShown: false,
        setExitSurveyHasBeenShown: (by) => {
          set(() => ({ exitSurveyHasBeenShown: by }));
        },
        npsScoreHasBeenSet: false,
        setNpsScoreHasBeenSet: (by) => {
          set(() => ({ npsScoreHasBeenSet: by }));
        },
        feedbackSurveyHasBeenShown: false,
        setFeedbackSurveyHasBeenShown: (by) => {
          set(() => ({ feedbackSurveyHasBeenShown: by }));
        },
        measurementHasBeenShown: false,
        setMeasurementHasBeenShown: (by) => {
          set(() => ({ measurementHasBeenShown: by }));
        },
        cookieType: CookieType.Decline,
        setCookieType: (by) => {
          set(() => ({ cookieType: by }));
        },
      }),
      {
        name: "visa-view",
      }
    )
  )
);

/**
 * ViewStore contains global view state that affects the current status of the app.
 * It does not persist between sessions.
 */
interface ViewStore {
  currentView?: CurrentView;
  setCurrentView: (m: CurrentView) => void;
  sideBarOpen: boolean;
  setSideBarOpen: (v: boolean) => void;
  showExitSurvey: boolean;
  setShowExitSurvey: (s: boolean) => void;
  showFeedbackSurvey: boolean;
  setShowFeedbackSurvey: (b: boolean) => void;
  onboardingCompleted: boolean;
  setOnboardingCompleted: (s: boolean) => void;
}

export const useViewStore = create<ViewStore>()((set) => ({
  currentView: undefined,
  setCurrentView: (by) => {
    set(() => ({ currentView: by }));
  },
  sideBarOpen: false,
  setSideBarOpen: (by) => {
    set(() => ({ sideBarOpen: by }));
  },
  showExitSurvey: false,
  setShowExitSurvey: (by) => {
    set(() => ({ showExitSurvey: by }));
  },
  showFeedbackSurvey: false,
  setShowFeedbackSurvey: (by) => {
    set(() => ({ showFeedbackSurvey: by }));
  },
  onboardingCompleted: false,
  setOnboardingCompleted: (by) => {
    set(() => ({ onboardingCompleted: by }));
  },
}));
